import type { AdminVehicleCatalogMakeDetailDto } from '@carcheck/shared';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ModelCreateDialog } from './ModelCreateDialog';
import { ModelEditDialog } from './ModelEditDialog';
import { ModelsTable } from './ModelsTable';
import {
  activeModelCount,
  filterModels,
  MODEL_TABS,
  tabCounts,
  type ModelTab,
} from '../_lib/modelFilter';
import type { UseCatalogWriteResult } from '../_lib/useCatalogWrite';
import { useModelToggle } from '../_lib/useModelToggle';

/**
 * «Модели» block of the make drawer (AUT-437): count + «N видно в
 * приложении», an in-memory search (name + aliases) and tabs, the table,
 * «Добавить модель» and the per-model dialog. `justCreated` — the make was
 * created a moment ago: the empty state says to add the first model.
 */
export interface ModelsSectionProps {
  make: AdminVehicleCatalogMakeDetailDto;
  syncing: boolean;
  justCreated: boolean;
  write: UseCatalogWriteResult;
  onOpenMake: (makeId: string) => void;
}

const SEARCH_MAX = 50;

const ModelsToolbar = (p: {
  query: string;
  onQuery: (q: string) => void;
  disabled: boolean;
  onAdd: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        size="small"
        fullWidth
        value={p.query}
        placeholder={t('vehicleCatalog.models.search')}
        onChange={(e) => p.onQuery(e.target.value)}
        slotProps={{
          htmlInput: { maxLength: SEARCH_MAX, 'aria-label': t('vehicleCatalog.models.search') },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        disabled={p.disabled}
        onClick={p.onAdd}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {t('vehicleCatalog.models.add')}
      </Button>
    </Box>
  );
};

export const ModelsSection = (p: ModelsSectionProps): ReactElement => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<ModelTab>('all');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const toggle = useModelToggle(p.make.id, p.write);
  const { models } = p.make;
  const counts = useMemo(() => tabCounts(models), [models]);
  const rows = useMemo(() => filterModels(models, tab, query), [models, tab, query]);
  const editing = models.find((m) => m.id === editingId) ?? null;
  const busy = p.write.busy || p.syncing;

  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        {t('vehicleCatalog.models.title')} · {models.length} ·{' '}
        {t('vehicleCatalog.models.visible', { n: activeModelCount(models) })} ·{' '}
        {t('vehicleCatalog.models.withGenerations', {
          n: models.filter((m) => m.generationCount > 0).length,
        })}
      </Typography>
      <ModelsToolbar
        query={query}
        onQuery={setQuery}
        disabled={busy}
        onAdd={() => setCreating(true)}
      />
      {models.length === 0 ? (
        <Alert severity={p.justCreated ? 'info' : 'warning'}>
          {p.justCreated ? t('vehicleCatalog.drawer.justCreated') : t('vehicleCatalog.models.none')}
        </Alert>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v: ModelTab) => setTab(v)} variant="scrollable">
            {MODEL_TABS.map((key) => (
              <Tab
                key={key}
                value={key}
                label={`${t(`vehicleCatalog.models.tabs.${key}`)} ${counts[key]}`}
              />
            ))}
          </Tabs>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('vehicleCatalog.models.notFound')}
            </Typography>
          ) : (
            <ModelsTable
              models={rows}
              disabled={busy}
              onToggle={(m, f) => void toggle(m, f)}
              onEdit={(m) => setEditingId(m.id)}
            />
          )}
        </>
      )}
      <ModelCreateDialog
        open={creating}
        makeId={p.make.id}
        write={p.write}
        onClose={() => setCreating(false)}
      />
      {editing ? (
        <ModelEditDialog
          makeId={p.make.id}
          makeName={p.make.name}
          model={editing}
          syncing={p.syncing}
          write={p.write}
          onOpenMake={p.onOpenMake}
          onClose={() => setEditingId(null)}
        />
      ) : null}
    </Box>
  );
};
